import DOMPurify from 'dompurify'
import React, { Fragment, ReactElement } from 'react'
import { StringUtils } from '../../../NonReact/utils/StringUtils'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import CFormatDate from '../../../NonReact/utils/DateTime'

export class LanguageUtils {
    public static sanitiseHTML(html?: string): string {
        return DOMPurify.sanitize(html ?? EMPTY_STRING)
    }

    /**
     * Format a language entry.
     *
     * @param entry - The language entry to be formatted.
     * @param interpolations - The interpolations to be used.
     * @returns
     */
    public static formatLanguageEntry(
        entry: string,
        ...interpolations: (Nullable<ReactElement> | string)[]
    ): ReactElement {
        // Sanitize the entry first to make sure errant code is not entered through the language.
        const formattedHTML: string = DOMPurify.sanitize(entry)
            // Replace ^^test^^ with <small>test</small>
            .replace(/\^\^(.*?)\^\^/g, '<small>$1</small>')
            // Replace ##!test##! with <ol>test</ol>
            .replace(/##!(.*?)##!/g, '<ol>$1</ol>')
            // Replace ##?test##? with <ul>test</ul>
            .replace(/##\?(.*?)##\?/g, '<ul>$1</ul>')
            // Replace ##test## with <li>test</li>
            .replace(/##(.*?)##/g, '<li>$1</li>')
            // Replace **Test** with <strong></strong>
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            // Replace //Test// with <p></p>
            .replace(/\/\/(.*?)\/\//g, '<p>$1</p>')
            // Replace {(http://url:_blank)Link} with <a href="http://url">Link</a>
            .replace(/\{\((.*?)(:(.*)?)\)(.*?)\}/g, "<a href='$1' target='$4' title='$2'>$2</a>")
            // Replace \n with <br />
            .replace(/\n/g, '<br/>')
            // Replace 2019-06-14T08:00:00.000ZL with "Wednesday, June 14, 2019 at 08:00:00 British Summer Time"
            .replace(/((\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}).000Z)L/g, (_: string, date: string): string =>
                CFormatDate(date, {
                    day: 'numeric',
                    hour: '2-digit',
                    hour12: false,
                    minute: 'numeric',
                    month: 'short',
                    second: 'numeric',
                    timeZoneName: 'long',
                    weekday: 'long',
                    year: 'numeric'
                })
            )
            // Replace 2019-06-14T08:00:00.000Z with "June 14, 2019 at 8:00 AM"
            .replace(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}).000Z/g, (date: string): string =>
                CFormatDate(date, {
                    hour: '2-digit',
                    minute: 'numeric',
                    hour12: true,
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                })
            )

        return (
            <>
                {formattedHTML
                    .split(StringUtils.INTERPOLATION_PLACEHOLDER)
                    .map((str: string, index: number): ReactElement => {
                        if (interpolations.length > index) {
                            return (
                                <Fragment key={`${str}-${index}`}>
                                    <div dangerouslySetInnerHTML={{ __html: str }}></div>
                                    {interpolations[index]}
                                </Fragment>
                            )
                        }

                        // Unfortunately using dangerouslySetInnerHTML is the only reliable way to render HTML from the language file.
                        return <div key={`${str}-${index}`} dangerouslySetInnerHTML={{ __html: str }}></div>
                    })}
            </>
        )
    }
}
